'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useConnectorStore } from '@/lib/connectors/store';
import { ConnectorForm } from './connector-form';
import { AuthConfigPanel } from './auth-config-panel';
import { OperationList } from './operation-list';
import { OperationEditor } from './operation-editor';
import { ConnectorTester } from './connector-tester';
import { ImportDialog } from './import-dialog';
import {
  ArrowLeft,
  Save,
  Play,
  Upload,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { useNavigate, useParams } from '@tanstack/react-router';

export function ConnectorBuilderPage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { connectorId?: string };
  const connectorId = params?.connectorId;

  const {
    currentConnector,
    connectorsLoading,
    connectorsError,
    operations,
    selectedOperation,
    loadConnector,
    loadOperations,
    createConnector,
    updateConnector,
    setSelectedOperation,
  } = useConnectorStore();

  const [activeTab, setActiveTab] = useState('settings');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (connectorId) {
      loadConnector(connectorId);
      loadOperations(connectorId);
    }
  }, [connectorId, loadConnector, loadOperations]);

  const handleSave = async () => {
    if (!currentConnector) return;

    setSaving(true);
    try {
      if (connectorId) {
        await updateConnector(connectorId, {
          display_name: currentConnector.display_name || undefined,
          description: currentConnector.description || undefined,
          base_url: currentConnector.base_url || undefined,
          auth_type: currentConnector.auth_type,
          auth_config: currentConnector.auth_config,
          default_headers: currentConnector.default_headers,
        });
      } else {
        const created = await createConnector({
          name: currentConnector.name,
          display_name: currentConnector.display_name || undefined,
          description: currentConnector.description || undefined,
          auth_type: currentConnector.auth_type,
          base_url: currentConnector.base_url || undefined,
        });
        navigate({ to: '/connectors' });
      }
    } catch (err) {
      console.error('Failed to save connector:', err);
    } finally {
      setSaving(false);
    }
  };

  if (connectorsLoading && connectorId) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate({ to: '/connectors' })}
            >
              <ArrowLeft className="size-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">
                {connectorId ? 'Edit Connector' : 'Create Connector'}
              </h1>
              {currentConnector?.name && (
                <p className="text-sm text-muted-foreground">
                  {currentConnector.display_name || currentConnector.name}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
              <Upload className="size-4 mr-2" />
              Import
            </Button>
            <Button
              variant="outline"
              onClick={() => setTestDialogOpen(true)}
              disabled={operations.length === 0}
            >
              <Play className="size-4 mr-2" />
              Test
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="size-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="size-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {connectorsError && (
        <Alert variant="destructive" className="mx-6 mt-4">
          <AlertTriangle className="size-4" />
          <AlertDescription>{connectorsError}</AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Operations List */}
        <div className="w-64 border-r overflow-y-auto">
          <OperationList
            connectorId={connectorId || ''}
            operations={operations}
            selectedId={selectedOperation?.id}
            onSelect={(op) => {
              setSelectedOperation(op);
              setActiveTab('operation');
            }}
          />
        </div>

        {/* Right Panel - Editor */}
        <div className="flex-1 overflow-y-auto p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="settings">Settings</TabsTrigger>
              <TabsTrigger value="auth">Authentication</TabsTrigger>
              <TabsTrigger value="operation" disabled={!selectedOperation}>
                Operation
              </TabsTrigger>
            </TabsList>

            <TabsContent value="settings" className="mt-6">
              <ConnectorForm />
            </TabsContent>

            <TabsContent value="auth" className="mt-6">
              <AuthConfigPanel />
            </TabsContent>

            <TabsContent value="operation" className="mt-6">
              {selectedOperation ? (
                <OperationEditor
                  connectorId={connectorId || ''}
                  operation={selectedOperation}
                />
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  Select an operation from the list to edit
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Dialogs */}
      <ImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        connectorId={connectorId || ''}
      />

      <ConnectorTester
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        connectorId={connectorId || ''}
        operations={operations}
      />
    </div>
  );
}
